import React from 'react';
import Select from 'react-select';

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    minHeight: 'unset',
    width: 'fit-content',
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#1e293b',
    cursor: 'pointer',
    paddingLeft: 0,
    display: 'flex',
    alignItems: 'center',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    flex: '0 1 auto',
    width: 'auto',
    maxWidth: 'unset',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    color: '#64748b',
    paddingRight: 0,
    marginLeft: 6,
    display: 'flex',
    alignItems: 'center',
    flex: '0 1 auto',
    width: 'auto',
    maxWidth: 'unset',
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: '#64748b',
    padding: 0,
    margin: 0,
    '&:hover': { color: '#2563eb' },
    display: 'flex',
    alignItems: 'center',
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '0.75rem',
    boxShadow: '0 4px 24px rgba(30,41,59,0.08)',
    fontSize: '1rem',
    fontWeight: 500,
    zIndex: 20,
    minWidth: '220px',
  }),
  option: (provided, state) => ({
    ...provided,
    background: state.isSelected
      ? '#2563eb'
      : state.isFocused
      ? '#f1f5f9'
      : 'white',
    color: state.isSelected ? 'white' : '#1e293b',
    borderRadius: '0.5rem',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#1e293b',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#64748b',
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '0.5rem',
  }),
};

export default function PeopleTable({ people, type = 'staff', onAdd, title, date, currentPage, totalPages, onPageChange, startIndex, endIndex, totalItems, onTypeChange }) {
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 flex flex-col h-[450px]">
      <div className="flex items-center justify-between mb-3">
        <div>
          {onTypeChange ? (
            <Select
              options={[
                { value: 'staff', label: 'Your Staff' },
                { value: 'students', label: 'Your Students' }
              ]}
              value={{ value: type, label: type === 'staff' ? 'Your Staff' : 'Your Students' }}
              onChange={(option) => (option && onTypeChange ? onTypeChange(option.value) : null)}
              styles={customSelectStyles}
              className="text-lg font-bold text-slate-800"
              isSearchable={false}
              components={{ IndicatorSeparator: () => null }}
            />
          ) : (
            <h3 className="text-lg font-bold text-slate-800">{title || (type === 'staff' ? 'Your Staff' : 'Your Students')}</h3>
          )}
          <p className="text-sm text-gray-500">{date || 'on Dec 19, 2025'}</p>
        </div>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded-xl font-semibold shadow hover:bg-blue-700 transition"
          onClick={onAdd}
        >
          Add {type === 'staff' ? 'Staff' : 'Student'} +
        </button>
      </div>
      <div className="overflow-x-auto overflow-y-auto flex-1 min-h-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-slate-50">
              <th className="py-2.5 px-4 text-left font-semibold text-slate-500">{type === 'staff' ? 'Staff' : 'Student'}</th>
              <th className="py-2.5 px-4 text-left font-semibold text-slate-500">Function</th>
              <th className="py-2.5 px-4 text-left font-semibold text-slate-500">Status</th>
              <th className="py-2.5 px-4 text-left font-semibold text-slate-500">{type === 'staff' ? 'Employed' : 'Enrolled'}</th>
              <th className="py-2.5 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {people.map((person, i) => (
              <tr key={i} className="border-b last:border-b-0 hover:bg-slate-50 transition">
                <td className="py-2.5 px-4 font-medium text-slate-700 flex items-center gap-2">
                  <img src={person.avatar} alt={person.name} className="w-7 h-7 rounded-full object-cover" />
                  <div>
                    <div className="font-medium text-slate-700">{person.name}</div>
                    <div className="text-xs text-slate-400">{person.email}</div>
                  </div>
                </td>
                <td className="py-2.5 px-4">
                  <>
                    <div className="font-medium text-slate-700">{person.function}</div>
                    <div className="text-xs text-slate-400">{person.class}</div>
                  </>
                </td>
                <td className="py-2.5 px-4">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${person.status === 'Present' ? 'bg-emerald-100 text-emerald-700' : person.status === 'Absent' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-500'}`}>{person.status}</span>
                </td>
                <td className="py-2.5 px-4">{person.empId || person.enrollId}</td>
                <td className="py-2.5 px-4 text-right">
                  <button className="text-blue-600 font-semibold hover:underline">Track</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination Controls */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
        <div className="text-sm text-slate-500">
          Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} staff
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded-lg text-sm font-medium ${currentPage === 1 ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
          >
            Previous
          </button>
          <div className="flex items-center gap-1">
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index + 1}
                onClick={() => onPageChange(index + 1)}
                className={`w-8 h-8 rounded-lg text-sm font-medium ${currentPage === index + 1 ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              >
                {index + 1}
              </button>
            ))}
          </div>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded-lg text-sm font-medium ${currentPage === totalPages ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
} 